// OP-3605 — Regras puras das baixas, espelho de `domain/cadastro.ts`
// (`podeDarBaixa`, `podeRecusarBaixa`) e de `validarMotivo` em
// `domain/formato.ts`. Gates visuais.
import { BaixaRow, StatusBaixa } from '../../../../types/estoque'

export const STATUS_BAIXA_UI: Record<StatusBaixa, { bg: string; color: string; label: string }> = {
  PROPOSTA: { bg: '#FEF3C7', color: '#92400E', label: 'Aguardando decisão' },
  APROVADA: { bg: '#F3F4F6', color: '#374151', label: 'Aprovada' },
  RECUSADA: { bg: '#FEE2E2', color: '#991B1B', label: 'Recusada' },
}

/**
 * O motivo precisa explicar — espelho de `validarMotivo`: 5 caracteres no
 * mínimo E pelo menos 4 distintos (ignorando espaços e caixa), 400 no máximo.
 * "xxxxxxxxxx" tem dez caracteres e não explica nada; é recusado.
 */
export function impedimentoMotivo(motivo: string): string | null {
  const m = motivo.trim()
  if (!m) return 'Escreva o motivo.'
  if (m.length < 5) return 'O motivo precisa de pelo menos 5 caracteres.'
  const distintos = new Set(m.toUpperCase().replace(/\s/g, '').split(''))
  if (distintos.size < 4) return 'O motivo precisa explicar: repetir o mesmo caractere não conta.'
  if (m.length > 400) return 'O motivo passa de 400 caracteres.'
  return null
}

/**
 * Aprovar a baixa — espelho de `podeDarBaixa`, na ordem do domínio.
 *
 * A peça só sai do patrimônio quando já está reconhecida como perdida ou
 * imprestável: extraviada ou sucata. Aprovar uma peça sadia seria apagar
 * patrimônio bom por decisão administrativa, e é isso que a regra impede.
 * O motivo que vale é o da PROPOSTA, não o da decisão.
 */
export const QUEM_PROPOE_NAO_DECIDE = 'Você propôs esta baixa: quem propõe não decide. Peça a decisão a outra pessoa da gerência.'

/** Espelho de `QUEM_PROPOE_NAO_DECIDE` do servidor: só barra quando os dois ids são conhecidos e iguais. */
export function propostaEhMinha(b: Pick<BaixaRow, 'propostoPorId'>, usuarioId: number | null | undefined): boolean {
  return usuarioId != null && b.propostoPorId != null && Number(b.propostoPorId) === Number(usuarioId)
}

export function impedimentoAprovarBaixa(
  b: Pick<BaixaRow, 'status' | 'cicloVida' | 'condicao' | 'motivo' | 'propostoPorId'>, podeDecidir: boolean, usuarioId?: number | null,
): string | null {
  if (!podeDecidir) return 'Decidir baixa é alçada da gerência (permissão "Decidir baixa").'
  if (propostaEhMinha(b, usuarioId)) return QUEM_PROPOE_NAO_DECIDE
  if (b.status !== 'PROPOSTA') return 'Esta proposta já foi decidida.'
  if (b.cicloVida === 'BAIXADO') return 'A peça já saiu do patrimônio.'
  if (b.cicloVida !== 'EXTRAVIADO' && b.condicao !== 'SUCATA') {
    return 'Só se dá baixa em peça extraviada ou sucateada. Esta ainda é patrimônio útil.'
  }
  if (!String(b.motivo || '').trim()) return 'A proposta veio sem motivo.'
  return null
}

/**
 * Recusar — espelho de `podeRecusarBaixa`: exige motivo da DECISÃO, e só um
 * texto não vazio (a régua de `validarMotivo` não se aplica aqui).
 */
export function impedimentoRecusarBaixa(
  b: Pick<BaixaRow, 'status' | 'propostoPorId'>, podeDecidir: boolean, motivoDecisao: string, usuarioId?: number | null,
): string | null {
  if (!podeDecidir) return 'Decidir baixa é alçada da gerência (permissão "Decidir baixa").'
  if (propostaEhMinha(b, usuarioId)) return QUEM_PROPOE_NAO_DECIDE
  if (b.status !== 'PROPOSTA') return 'Esta proposta já foi decidida.'
  if (!motivoDecisao.trim()) return 'Recusar exige dizer por quê.'
  return null
}

/** Anexos da decisão: até 5, imagem ou PDF, 8 MB cada (`validarAnexos`). */
export const ANEXO_MAX = 5
export const ANEXO_BYTES_MAX = 8 * 1024 * 1024
export const ANEXO_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf']

export function impedimentoAnexo(f: { type: string; size: number }, jaEnviados: number): string | null {
  if (jaEnviados >= ANEXO_MAX) return `São no máximo ${ANEXO_MAX} anexos por decisão.`
  if (ANEXO_MIMES.indexOf(f.type) < 0) return 'Tipo não aceito: use imagem (JPEG, PNG, WEBP, HEIC) ou PDF.'
  if (!f.size) return 'O arquivo está vazio.'
  if (f.size > ANEXO_BYTES_MAX) return 'O arquivo passa de 8 MB.'
  return null
}
